import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";
import { TIP_ALONG, TIP_PIP, TipShape } from "./tip-shape";

/*
 * Подсказка у органа управления.
 *
 * Берётся вместо нативного `title`, которого в приложении было восемь десятков.
 * У него три беды разом: вид рисует система — чужим шрифтом и мимо темы,
 * задержка не наша, а на касании его нет вовсе. Последнее здесь весомо: половина
 * значков в полосе звонка и в шапке канала подписана только им, и на планшете
 * они молчали.
 *
 * Вид — материал «Почты», без стекла движка, одной фигурой с хвостиком
 * (`TipShape`): хвостик — часть контура и смотрит в середину кнопки, по нему
 * же идут кромка и тень. Стрелка Radix стоит, но спрятана: она только
 * говорит, где хвостику быть. Появляется пружиной от кончика хвостика,
 * подпись — из лёгкого размытия.
 *
 * Провайдер стоит один на приложение (`app.tsx`): он держит общую задержку и
 * правило «вторая подсказка подряд показывается сразу» — без него каждая ждёт
 * свою паузу, и обход полосы значков превращается в череду ожиданий.
 */
function TooltipProvider({
  /*
   * Задержки набора: 420 мс до первой подсказки — она не выскакивает на
   * каждый пролёт курсора над полосой значков, — и полсекунды, в которые
   * следующая показывается сразу.
   */
  delayDuration = 420,
  skipDelayDuration = 500,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

function Tooltip(props: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger(props: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  // Зазор от кончика хвостика до кнопки. Длину хвостика (высоту стрелки) Radix прибавляет к нему сам.
  sideOffset = 6,
  collisionPadding = 8,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          // Меры набора: высота 30, 13/1.35, поля 12, текст не шире 220.
          "z-50 flex w-fit max-w-[248px] min-h-[30px] items-center justify-center px-[12px] py-[6px] lui-tip",
          "text-[13px]/[1.35] font-medium tracking-[-0.005em] text-foreground text-center",
          className,
        )}
        /*
         * Radix кладёт рядом невидимую копию содержимого для читалки экрана, и
         * с формой внутри копия завела бы вторую фигуру и вторую стрелку.
         * Строку отдаём подписью — тогда копия берёт её, а не детей.
         */
        aria-label={typeof children === "string" ? children : undefined}
        {...props}
      >
        <TipShape />
        <span className="lui-tip-label">{children}</span>
        <TooltipPrimitive.Arrow width={TIP_ALONG} height={TIP_PIP} className="lui-tip-arrow" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
